import { motion, AnimatePresence } from 'framer-motion';

interface VictoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: {
    tiempo: number;
    movimientos?: number;
    aciertos?: number; // Porcentaje de aciertos (para juegos como ITV Express)
    reaccion?: number; // Tiempo de reacción medio en segundos (para ITV Express)
  };
}

/**
 * Modal de victoria estandarizado para todos los juegos de Neureka
 * Replica exactamente el comportamiento de partials/victory_modal.php
 */
export default function VictoryModal({
  isOpen,
  onClose,
  stats,
}: VictoryModalProps) {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay oscuro */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ 
              opacity: 1, 
              scale: [0.3, 1.05, 1],
              transition: { duration: 0.5 }
            }}
            exit={{ opacity: 0, scale: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div
              className="victory-modal bg-white rounded-[20px] shadow-2xl max-w-md w-full mx-4 pointer-events-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Contenido del modal */}
              <div className="text-center px-10 py-10">
                {/* Título */}
                <h2 className="text-5xl font-bold mb-5" style={{ color: '#4CAF50' }}>
                  🎉 ¡Enhorabuena!
                </h2>
                
                {/* Descripción */}
                <p className="text-xl text-gray-600 mb-2.5">
                  Has completado el nivel correctamente
                </p>
                
                {/* Tiempo (siempre visible) */}
                <p className="mb-2.5 text-lg text-gray-800">
                  <strong>⏱️ Tiempo:</strong>{' '}
                  <span>{formatTime(stats.tiempo)}</span>
                </p>
                
                {/* Movimientos (opcional) */}
                {stats.movimientos !== undefined && stats.movimientos !== null && (
                  <p className="mb-2.5 text-lg text-gray-800">
                    <strong>🎯 Movimientos:</strong>{' '}
                    <span>{stats.movimientos}</span>
                  </p>
                )}
                
                {/* Aciertos (opcional - para juegos como ITV Express) */}
                {stats.aciertos !== undefined && stats.aciertos !== null && (
                  <p className="mb-2.5 text-lg text-gray-800">
                    <strong>🎯 Aciertos:</strong>{' '}
                    <span>{stats.aciertos}%</span>
                  </p>
                )}
                
                {/* Reacción media (opcional - para juegos como ITV Express) */}
                {stats.reaccion !== undefined && stats.reaccion !== null && (
                  <p className="mb-5 text-lg text-gray-800">
                    <strong>⚡ Reacción media:</strong>{' '}
                    <span>{stats.reaccion.toFixed(2)}s</span>
                  </p>
                )}
                
                {/* Botón Continuar */}
                <button
                  onClick={onClose}
                  className="text-white font-semibold text-base px-10 py-3 rounded-[10px] border-none mt-5 hover:opacity-90 transition-opacity"
                  style={{ background: '#667eea' }}
                >
                  Continuar
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
